import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Youtube, Facebook, Instagram, Twitter, Smartphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function Footer() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const socials = [
    { url: settings?.youtube_url, Icon: Youtube, label: "YouTube" },
    { url: settings?.facebook_url, Icon: Facebook, label: "Facebook" },
    { url: settings?.instagram_url, Icon: Instagram, label: "Instagram" },
    { url: settings?.x_url, Icon: Twitter, label: "X" },
  ].filter((s) => s.url);

  return (
    <footer className="mt-20 bg-charcoal text-white">
      <div className="h-1 w-full bg-gradient-to-r from-brand via-brandsec to-crimson" />
      <div className="mx-auto max-w-6xl px-6 py-14 grid gap-8 sm:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Leaf className="text-brandsec" size={20} />
            <span className="font-display text-2xl text-white">Altos World</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brandsec mb-2" data-testid="footer-authorised-distributor">
            An Authorised Distributor of Altos Enterprises Limited
          </p>
          <p className="text-sm text-white/60 leading-relaxed max-w-xs">
            Trusted online store for herbal supplements & natural skincare — Cuttack Super Zone.
          </p>
          {socials.length > 0 && (
            <div className="flex gap-2 mt-4" data-testid="footer-socials">
              {socials.map(({ url, Icon, label }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:border-brandsec hover:bg-white/5 transition-colors">
                  <Icon size={17} />
                </a>
              ))}
            </div>
          )}
          <Link to="/register" data-testid="footer-register" className="inline-flex mt-4 text-sm font-semibold text-brandsec hover:text-white transition-colors">Become a Direct Seller →</Link>

          {settings?.android_app_url && (
            <a href={settings.android_app_url} target="_blank" rel="noopener noreferrer" data-testid="footer-download-app" className="mt-5 flex items-center gap-3 rounded-xl border border-white/20 hover:border-brandsec hover:bg-white/5 px-4 py-3 max-w-xs transition-colors">
              <Smartphone className="text-brandsec shrink-0" size={22} />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Download our Android App</p>
                <p className="text-[11px] text-white/55">Shop faster on your phone</p>
              </div>
            </a>
          )}
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider text-brandsec font-semibold mb-3">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="text-white/75 hover:text-brandsec transition-colors">All Products</Link></li>
            <li><Link to="/concerns" className="text-white/75 hover:text-brandsec transition-colors">Shop by Concern</Link></li>
            <li><Link to="/blogs" className="text-white/75 hover:text-brandsec transition-colors">Blogs & Vlogs</Link></li>
            <li><Link to="/orders" className="text-white/75 hover:text-brandsec transition-colors">Track My Orders</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-wider text-brandsec font-semibold mb-3">Company</h4>
          <ul className="space-y-2 text-sm mb-4">
            <li><Link to="/about" className="text-white/75 hover:text-brandsec transition-colors">About Us</Link></li>
            <li><Link to="/terms" className="text-white/75 hover:text-brandsec transition-colors">Terms & Conditions</Link></li>
            <li><Link to="/contact" className="text-white/75 hover:text-brandsec transition-colors">Contact Us</Link></li>
          </ul>
          <h4 className="text-xs uppercase tracking-wider text-brandsec font-semibold mb-3">Contact</h4>
          <ul className="space-y-2 text-sm text-white/60">
            <li>+91 77354 54828</li>
            <li>altosworldonline@gmail.com</li>
            <li>Cuttack, Odisha, India</li>
            <li><Link to="/admin" className="text-white/40 hover:text-brandsec text-xs transition-colors">Admin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-white/50">
        © {new Date().getFullYear()} Altos World Online Store. All rights reserved.
      </div>
    </footer>
  );
}
